import {
  IconDeviceDesktop,
  IconLogout,
  IconMoon,
  IconSun,
  type Icon as TablerIcon,
} from '@tabler/icons-react'

import { signOut } from '@/auth/api'
import { useAuth } from '@/auth/useAuth'
import { railControlClass } from '@/components/rail-settings'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { type Theme, useTheme } from '@/providers/ThemeProvider'

const THEMES: { id: Theme; icon: TablerIcon; label: string }[] = [
  { id: 'light', icon: IconSun, label: 'Light' },
  { id: 'dark', icon: IconMoon, label: 'Dark' },
  { id: 'system', icon: IconDeviceDesktop, label: 'System' },
]

/** First letters of the name (or the email) for the avatar fallback. */
export function initials(name: string, email: string): string {
  const fromName = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
  return (fromName || email[0] || 'U').toUpperCase()
}

/** The signed-in person as the chrome shows them: a name that is never empty. */
export function useDisplayUser() {
  const { user } = useAuth()
  return {
    name:
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      user?.email?.split('@')[0] ||
      'Account',
    email: user?.email ?? '',
    avatar: user?.profilePictureUrl ?? '',
  }
}

/**
 * The account control: the avatar ALONE, at the foot of the rail (and in the
 * phone sheet's footer, where there is no rail). Pressing it opens the account
 * menu — the name and email, the Theme pick and sign-out — beside the rail.
 * Nothing that goes nowhere: Billing and Notifications are absent until there
 * is something behind them, and Settings is the gear right above the avatar.
 * The theme lives here, and only here in the chrome (Light | Dark | System, a
 * radio group in a submenu); the palette offers it too.
 */
export function NavUser({
  side = 'right',
  align = 'end',
  className,
}: {
  side?: 'right' | 'top'
  align?: 'start' | 'end'
  className?: string
}) {
  const user = useDisplayUser()
  const fallback = initials(user.name, user.email)
  const { theme, setTheme } = useTheme()
  const currentTheme = THEMES.find((t) => t.id === theme) ?? THEMES[0]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={user.email ? `Account menu for ${user.email}` : 'Account menu'}
        // NAMED group: the avatar answers ITS trigger's hover and open state,
        // never some ancestor that happens to carry a bare `group`.
        className={cn(railControlClass, 'group/user', className)}
      >
        {/* Greyscale at rest so the chrome stays monochrome; the photo comes
            back to life under the pointer, with the brand hairline so the
            initials fallback answers too. */}
        <Avatar className="size-8 rounded-lg grayscale ring-1 ring-transparent transition-[filter,box-shadow] duration-[var(--motion-state)] ease-out group-hover/user:grayscale-0 group-hover/user:ring-ring/60 group-data-[state=open]/user:grayscale-0 group-data-[state=open]/user:ring-ring/60 motion-reduce:transition-none">
          <AvatarImage src={user.avatar} alt="" />
          <AvatarFallback className="rounded-lg text-xs">{fallback}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg motion-reduce:animate-none"
        side={side}
        align={align}
        sideOffset={8}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="rounded-lg">{fallback}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user.name}</span>
              <span className="truncate text-xs text-muted-foreground">{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <currentTheme.icon />
              Theme
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-40 rounded-lg motion-reduce:animate-none">
              <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
                {THEMES.map((option) => (
                  <DropdownMenuRadioItem key={option.id} value={option.id}>
                    <option.icon aria-hidden />
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void signOut()}>
          <IconLogout />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
